import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid as MuiGrid,
  Divider
} from '@mui/material';

const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const ScheduleInput = ({ scheduleData = {}, onChange, disabled = false }) => {

  // Log the received scheduleData prop and disabled state
  console.log('[ScheduleInput.js] Received scheduleData:', scheduleData, 'disabled:', disabled); // Enhanced log

  // Restored handleChange with mutual exclusivity logic
  const handleChange = (fieldPath, value, type) => {
    let finalValue = value;
    if (type === 'checkbox') {
      finalValue = value; // Use the boolean directly
    }
    
    console.log(`[ScheduleInput.js] handleChange called with fieldPath: ${fieldPath}, value:`, finalValue, 'disabled:', disabled);

    // Prevent changes if disabled
    if (disabled) {
      console.log('[ScheduleInput.js] Not updating due to disabled state');
      return;
    }
    
    // Use the parent onChange to update the state with the path
    onChange(fieldPath, finalValue);

    // Handle mutual exclusivity between sameEveryDay and sameTimeSelectedDays
    if (fieldPath === 'sameEveryDay' && finalValue === true) {
      // Don't add 'schedule.' prefix - the parent component will add it
      onChange('sameTimeSelectedDays', false);
    } else if (fieldPath === 'sameTimeSelectedDays' && finalValue === true) {
      // Don't add 'schedule.' prefix - the parent component will add it
      onChange('sameEveryDay', false);
    }
  };

  // Directly access values from scheduleData prop
  const open247 = scheduleData?.open247 ?? false;
  const sameEveryDay = scheduleData?.sameEveryDay ?? false;
  const sameTimeSelectedDays = scheduleData?.sameTimeSelectedDays ?? false;

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={open247} // Use direct value
            onChange={(e) => handleChange('open247', e.target.checked, 'checkbox')}
            disabled={disabled}
            size="small"
          />
        }
        label="Open 24/7"
        sx={{ height: '30px' }}
      />

      {!open247 && (
        <Box sx={{ pl: 2, mt: 1, borderLeft: '2px solid #eee' }}>
          <Typography variant="subtitle2" gutterBottom>Detailed Hours</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={sameEveryDay} // Use direct value
                // Pass path relative to schedule object to internal handleChange
                onChange={(e) => handleChange('sameEveryDay', e.target.checked, 'checkbox')}
                disabled={disabled}
                size="small"
              />
            }
            label="Same Time Every Day"
            sx={{ height: '30px' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={sameTimeSelectedDays} // Use direct value
                // Pass path relative to schedule object to internal handleChange
                onChange={(e) => {
                  console.log('[ScheduleInput.js] sameTimeSelectedDays checkbox clicked:', e.target.checked);
                  handleChange('sameTimeSelectedDays', e.target.checked, 'checkbox');
                }}
                disabled={disabled || sameEveryDay} // Disable if sameEveryDay is checked
                size="small"
              />
            }
            label="Same Time For Selected Days"
            sx={{ height: '30px' }}
          />

          {/* Conditional Time Inputs */}
          {sameEveryDay ? (
            // Single pair for everyday
            <MuiGrid container spacing={1} alignItems="center">
              <MuiGrid item xs={6}>
                <TextField fullWidth label="Open Time (HH:MM)" type="time" size="small" margin="dense" InputLabelProps={{ shrink: true }}
                  value={scheduleData?.everyDayOpen ?? ''} // Read nested prop directly
                  onChange={(e) => handleChange('everyDayOpen', e.target.value)} // Pass relative path
                  disabled={disabled}
                />
              </MuiGrid>
              <MuiGrid item xs={6}>
                <TextField fullWidth label="Close Time (HH:MM)" type="time" size="small" margin="dense" InputLabelProps={{ shrink: true }}
                  value={scheduleData?.everyDayClose ?? ''} // Read nested prop directly
                  onChange={(e) => handleChange('everyDayClose', e.target.value)} // Pass relative path
                  disabled={disabled}
                />
              </MuiGrid>
            </MuiGrid>
          ) : sameTimeSelectedDays ? (
             // Single pair + day checkboxes
             <>
               <MuiGrid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <MuiGrid item xs={6}>
                   <TextField fullWidth label="Open Time (HH:MM)" type="time" size="small" margin="dense" InputLabelProps={{ shrink: true }}
                     value={scheduleData?.everyDayOpen ?? ''} // Read nested prop directly
                     onChange={(e) => handleChange('everyDayOpen', e.target.value)} // Pass relative path
                     disabled={disabled}
                   />
                 </MuiGrid>
                 <MuiGrid item xs={6}>
                   <TextField fullWidth label="Close Time (HH:MM)" type="time" size="small" margin="dense" InputLabelProps={{ shrink: true }}
                     value={scheduleData?.everyDayClose ?? ''} // Read nested prop directly
                     onChange={(e) => handleChange('everyDayClose', e.target.value)} // Pass relative path
                     disabled={disabled}
                   />
                 </MuiGrid>
                </MuiGrid>
               <Divider sx={{ my: 1 }} />
               {daysOfWeek.map(day => (
                 <Box key={day} sx={{ mb: 1 }}>
                   <FormControlLabel
                     control={
                       <Checkbox
                         checked={scheduleData?.days?.[day]?.isOpen ?? false} // Read nested prop directly
                         onChange={(e) => {
                           console.log(`[ScheduleInput.js] Day checkbox clicked for ${day}:`, e.target.checked);
                           handleChange(`days.${day}.isOpen`, e.target.checked, 'checkbox');
                         }} // Pass relative path
                         disabled={disabled}
                         size="small"
                       />
                     }
                     label={day.charAt(0).toUpperCase() + day.slice(1)} sx={{ height: '30px' }}
                   />
                 </Box>
               ))}
             </>
          ) : (
            // Individual day inputs
            daysOfWeek.map(day => (
              <Box key={day} sx={{ mb: 1 }}>
                 <FormControlLabel
                   control={
                     <Checkbox
                       checked={scheduleData?.days?.[day]?.isOpen ?? false} // Read nested prop directly
                       onChange={(e) => {
                         console.log(`[ScheduleInput.js] Individual day checkbox clicked for ${day}:`, e.target.checked);
                         handleChange(`days.${day}.isOpen`, e.target.checked, 'checkbox');
                       }} // Pass relative path
                       disabled={disabled}
                       size="small"
                    />
                  }
                  label={day.charAt(0).toUpperCase() + day.slice(1)} sx={{ height: '30px', minWidth: '120px' }}
                />
                 {scheduleData?.days?.[day]?.isOpen && ( // Read nested prop directly
                   <MuiGrid container spacing={1} alignItems="center" sx={{ display: 'inline-flex', width: 'calc(100% - 130px)', verticalAlign: 'middle' }}>
                     <MuiGrid item xs={6}>
                       <TextField fullWidth label="Open" type="time" size="small" margin="none" InputLabelProps={{ shrink: true }}
                         value={scheduleData?.days?.[day]?.open ?? ''} // Read nested prop directly
                         onChange={(e) => handleChange(`days.${day}.open`, e.target.value)} // Pass relative path
                         disabled={disabled}
                       />
                     </MuiGrid>
                     <MuiGrid item xs={6}>
                       <TextField fullWidth label="Close" type="time" size="small" margin="none" InputLabelProps={{ shrink: true }}
                         value={scheduleData?.days?.[day]?.close ?? ''} // Read nested prop directly
                         onChange={(e) => handleChange(`days.${day}.close`, e.target.value)} // Pass relative path
                         disabled={disabled}
                       />
                    </MuiGrid>
                  </MuiGrid>
                )}
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default ScheduleInput;
